import axios from 'axios';
import api from '../core';
import type { SecuritySettings } from '../../types/security';
import type { OAuthUserInfo } from '../../types/oauth';

/**
 * API functions for authentication
 * @namespace authApi
 */
export const authApi = {
  /**
   * Logs in a user
   * @function login
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} [captchaToken] - Captcha token
   * @returns {Promise<any>}
   */
  login: async (email: string, password: string, captchaToken?: string) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    if (captchaToken) {
      formData.append('captcha_token', captchaToken);
    }
    const response = await api.post('/auth/token', formData, {
      headers: {
        // Ensure content-type is removed for FormData
        'Content-Type': undefined
      }
    });
    return response.data;
  },

  /**
   * Registers a new user
   * @function register
   * @param {{ name: string; email: string; password: string; captcha_token?: string }} data - User registration data
   * @returns {Promise<any>}
   */
  register: async (data: { name: string; email: string; password: string; captcha_token?: string }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  /**
   * Gets the current user
   * @function getCurrentUser
   * @returns {Promise<any>}
   */
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Gets the plan overview
   * @function getPlanOverview
   * @returns {Promise<any>}
   */
  getPlanOverview: async () => {
    const response = await api.get('/reports/users/plan-overview');
    return response.data;
  },

  /**
   * Changes the user's password
   * @function changePassword
   * @param {{ current_password?: string; new_password: string }} data - Password change data
   * @returns {Promise<any>}
   */
  changePassword: async (data: { current_password?: string; new_password: string }) => {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },

  /**
   * Gets risky IP addresses
   * @function getRiskIPs
   * @param {number} [skip=0] - Number of IPs to skip
   * @param {number} [limit=10] - Number of IPs to limit to
   * @returns {Promise<any>}
   */
  getRiskIPs: async (skip: number = 0, limit: number = 10) => {
    const response = await api.get('/security/risk/ips', {
      params: { skip, limit }
    });
    return response.data;
  },

  /**
   * Resets statistics for a given IP address
   * @function resetIpStats
   * @param {string} ipAddress - IP address to reset
   * @returns {Promise<any>}
   */
  resetIpStats: async (ipAddress: string) => {
    const response = await api.post(`/security/risk/ips/${ipAddress}/reset`);
    return response.data;
  },

  /**
   * Unblocks a given IP address
   * @function unblockIp
   * @param {string} ipAddress - IP address to unblock
   * @returns {Promise<any>}
   */
  unblockIp: async (ipAddress: string) => {
    const response = await api.post(`/security/blocked/ips/${ipAddress}/unblock`);
    return response.data;
  },

  /**
   * Unblocks a user's IP address
   * @function unblockIpUser
   * @param {string} ipAddress - IP address to unblock
   * @param {string} userEmail - User email
   * @returns {Promise<any>}
   */
  unblockIpUser: async (ipAddress: string, userEmail: string) => {
    const response = await api.post(`/security/blocked/users/${userEmail}/ips/${ipAddress}/unblock`);
    return response.data;
  },

  /**
   * Updates security settings
   * @function updateSecuritySettings
   * @param {SecuritySettings} settings - Security settings to update
   * @returns {Promise<any>}
   */
  updateSecuritySettings: async (settings: SecuritySettings) => {
    const response = await api.put('/security/settings', settings);
    return response.data;
  },

  /**
   * Gets security status
   * @function getSecurityStatus
   * @returns {Promise<any>}
   */
  getSecurityStatus: async () => {
    const response = await api.get('/auth/security-status');
    
    return response.data;
  },

  /**
   * Starts OAuth login
   * @function startOAuthLogin
   * @param {string} provider - OAuth provider
   * @param {string} [returnTo] - URL to return to after OAuth
   * @returns {Promise<any>}
   */
  startOAuthLogin: async (provider: string, returnTo?: string) => {
    // Use https for OAuth callbacks
    const protocol = 'https:';
    const stateData = {
      provider,
      action: 'login',
      redirect_url: `${protocol}//${window.location.host}/oauth-callback/${provider}`,
      return_to: returnTo
    };
    const params = new URLSearchParams({
      redirect_url: stateData.redirect_url,
      provider,
      action: 'login',
      state: encodeURIComponent(JSON.stringify(stateData))
    });
    const response = await api.get(`/auth/oauth/${provider}/authorize?${params}`);
    return response.data;
  },

  /**
   * Handles OAuth callback
   * @function handleOAuthCallback
   * @param {string} provider - OAuth provider
   * @param {string} code - Callback code
   * @param {string} state - Callback state
   * @returns {Promise<any>}
   */
  handleOAuthCallback: async (provider: string, code: string, state: string) => {
    try {
      const response = await api.get(`/auth/oauth/${provider}/callback`, {
        params: { code, state }
      });
      return response.data;
    } catch (error: unknown) {
      // Rethrow the error directly to preserve the response data
      if (axios.isAxiosError(error)) {
        throw error;
      }
      throw new Error('Authentication failed');
    }
  },

  /**
   * Links an OAuth provider
   * @function linkOAuthProvider
   * @param {string} provider - OAuth provider
   * @returns {Promise<any>}
   */
  linkOAuthProvider: async (provider: string) => {
    // Use https for OAuth callbacks
    const protocol = 'https:';
    const params = new URLSearchParams({
      redirect_url: `${protocol}//${window.location.host}/oauth-callback/${provider}`,
      provider,
      action: 'link'
    });
    const response = await api.get(`/auth/oauth/${provider}/authorize?${params}`);
    return response.data;
  },

  /**
   * Unlinks an OAuth provider
   * @function unlinkOAuthProvider
   * @param {string} provider - OAuth provider
   * @returns {Promise<any>}
   */
  unlinkOAuthProvider: async (provider: string) => {
    const response = await api.post(`/auth/oauth/${provider}/link`, {
      action: 'unlink'
    });
    return response.data;
  },

  /**
   * Changes the user's plan
   * @function changePlan
   * @param {string} plan - Plan type (free, pro, enterprise)
   * @returns {Promise<any>}
   */
  changePlan: async (plan: string) => {
    const response = await api.post('/plans/change', { plan });
    return response.data;
  },

  /**
   * Creates a new account using OAuth data
   * @function createOAuthAccount
   * @param {OAuthUserInfo} oauthData - OAuth user information
   * @returns {Promise<any>}
   */
  createOAuthAccount: async (oauthData: OAuthUserInfo) => {
    const response = await api.post('/auth/oauth/create', { oauth_data: oauthData });
    return response.data;
  },

  /**
   * Requests a password reset email
   * @function requestPasswordReset
   * @param {string} email - User's email address
   * @param {string} captchaToken - Captcha verification token
   * @returns {Promise<any>}
   */
  requestPasswordReset: async (email: string, captchaToken: string) => {
    const response = await api.post('/auth/request-password-reset', {
      email,
      captcha_token: captchaToken
    });
    return response.data;
  },

  /**
   * Resets password using reset token
   * @function resetPassword
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise<any>}
   */
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  /**
   * Delete user account
   * @function deleteAccount
   * @param {{ currentPassword: string; confirmDeletion: boolean }} data - Delete account data
   * @returns {Promise<any>}
   */
  deleteAccount: async (data: { currentPassword: string; confirmDeletion: boolean }) => {
    const response = await api.post('/auth/delete-account', {
      current_password: data.currentPassword,
      confirm_deletion: data.confirmDeletion
    });
    return response.data;
  },

  /**
   * Cancel scheduled account deletion
   * @function cancelDeletion
   * @param {{ currentPassword: string; confirmDeletion: boolean }} data - Cancel deletion data
   * @returns {Promise<any>}
   */
  cancelDeletion: async (data: { currentPassword: string; confirmDeletion: boolean }) => {
    const response = await api.post('/auth/cancel-deletion', {
      current_password: data.currentPassword,
      confirm_deletion: data.confirmDeletion
    });
    return response.data;
  },

  /**
   * Confirms user's email address
   * @function confirmEmail
   * @param {string} email - User's email address
   * @param {string} code - Email confirmation code
   * @returns {Promise<any>}
   */
  confirmEmail: async (email: string, code: string) => {
    const response = await api.post('/auth/confirm-email', {
      email,
      code
    });
    return response.data;
  },

  /**
   * Resends confirmation email
   * @function resendConfirmation
   * @param {string} email - User's email address
   * @returns {Promise<any>}
   */
  resendConfirmation: async (email: string) => {
    const response = await api.post('/auth/resend-confirmation', {
      email
    });
    return response.data;
  },

  /**
   * Refreshes access token if it's more than a week old
   * @function refreshToken
   * @param {string} token - Current access token
   * @returns {Promise<{access_token: string}>}
   */
  refreshToken: async (token: string) => {
    const response = await api.post('/auth/refresh', { token });
    return response.data;
  },

  /**
   * Invalidates all user sessions by changing their SECRET_KEY
   * @function invalidateAllSessions
   * @returns {Promise<any>}
   */
  invalidateAllSessions: async () => {
    const response = await api.post('/auth/invalidate-all-sessions');
    return response.data;
  }
};
